import { memo } from 'react';
import { getErrorStatus, useRestOperations } from '../../rest/restOperations';
import { Routes } from '../../../routes/routes';
import { ApiUrls } from '../../../../api/api/apiUrls';
import { successNotification, errorNotification } from '../../../utils/notifications';
import { useTranslation } from '../../i18n/useTranslation';
import { getLocallyStoredUser, setLocallyStoredUser } from '../../../utils/utils';
import { baseUser, getLoggedInUser } from '../../../../environment/env.dev';
import BeamButton from './BeamButton';
import { ButtonSize, ButtonVariant, WorkbenchTheme } from '@workbench/core';
import { isDevOrLocalEnv } from '../../../configuration/confUtils';
import { useClientCompanyRegistrationContext } from '../../../components/client/companyRegistration/context/ClientCompanyRegistrationContext';
import { UserInfoDto } from '../../../api';

interface LoginButtonProps {
    variant: ButtonVariant;
    size?: ButtonSize;
    theme?: WorkbenchTheme;
    className?: string;
    isLoginMenu?: boolean;
}

const POST_LOGIN_FLAG = 'afterLoginRedirect';

const LoginButton = (props: LoginButtonProps) => {
    const { doGetRequest } = useRestOperations<UserInfoDto>();
    const { setUserInfo, setLoginTriggered } = useClientCompanyRegistrationContext();
    const translate = useTranslation();

    const handleLoginUser = async () => {
        if (!isDevOrLocalEnv()) {
            sessionStorage.setItem(POST_LOGIN_FLAG, '1');
            window.location.href = `${window.location.origin}/${Routes.oidc_login}`;
        } else {
            // Forcing logging in by saving base user into local storage if empty
            if (getLocallyStoredUser()?.id === undefined) {
                setLocallyStoredUser(baseUser);
                getLoggedInUser();
                sessionStorage.setItem(POST_LOGIN_FLAG, '1');
                // Reload the website
                window.location.href = '/';
            }

            const result = await doGetRequest({ queryString: ApiUrls.userApi.userInfo });

            if (!result) {
                const errorStatus = getErrorStatus();
                if (errorStatus !== 403 && errorStatus !== 401) {
                    errorNotification(translate('notification_server_downtime'));
                }
            } else {
                setUserInfo(result as UserInfoDto);
                successNotification(translate('notification_successful_login'));
                setLoginTriggered(true);
            }
        }
    };
    return (
        <BeamButton
            variant={props.variant}
            size={props.size}
            className={props.className}
            theme={props.theme}
            onClick={handleLoginUser}
        >
            {props.isLoginMenu ? translate('common_login_menu') : translate('common_login')}
        </BeamButton>
    );
};

export default memo(LoginButton);
